import type { Onboarding } from "@/state/onboarding";
import { i } from "@instantdb/react";

const _schema = i.schema({
  entities: {
    // System entities
    $files: i.entity({
      path: i.string().unique().indexed(),
      url: i.any(),
    }),
    $users: i.entity({
      email: i.string().unique().indexed(),
    }),
    // Authentication entities
    users: i.entity({
      name: i.string().optional(),
      avatar: i.string().optional(),
      email: i.string().unique(),
      emailVerified: i.boolean().optional(),
      language: i.string().optional(),
      aiLanguage: i.string().optional(),
      aiSuggestions: i.number().optional(),
      proStatus: i.string().optional(),
      plan: i.string().optional(),
      stripeCustomerId: i.string().optional(),
      stripeSubscriptionId: i.string().optional(),
      nextBillingDate: i.string().optional(),
      subscriptionProvider: i.string<"android" | "ios" | "web">().optional(),
      androidPurchaseToken: i.string().optional(),
      marketingMails: i.json<string[]>().optional(), // list of email IDs that have been sent to the user
      allowMarketingMails: i.boolean().optional(),
      providerId: i.string().optional(),
      timeFormat: i.string<"12h" | "24h">().optional(),
      weekStartsOn: i.number().optional(),
      timezone: i.string().optional(),
      onboarding: i.json<Onboarding>().optional(),
      onboardingCompleted: i.boolean().optional(),
      notificationSettings: i
        .json<{
          todaysRoutines?: boolean;
          routineReminder?: boolean;
        }>()
        .optional(),
      fcmTokenWeb: i.string().optional(),
      fcmTokenIOS: i.string().optional(),
      fcmTokenAndroid: i.string().optional(),
      hasBeenAskedForReview: i.boolean().optional(),
      createdAt: i.date(),
      updatedAt: i.date(),
    }),
    sessions: i.entity({
      userId: i.string().indexed(),
      expiresAt: i.date().indexed(),
      ipAddress: i.string(),
      token: i.string(),
      updatedAt: i.date(),
      userAgent: i.string(),
      createdAt: i.date(),
    }),
    accounts: i.entity({
      userId: i.string().indexed(),
      accessToken: i.string().optional(),
      accessTokenExpiresAt: i.date().optional(),
      accountId: i.string(),
      idToken: i.string().optional(),
      password: i.string().optional(),
      providerId: i.string(),
      refreshToken: i.string().optional(),
      refreshTokenExpiresAt: i.date().optional(),
      scope: i.string().optional(),
      updatedAt: i.date(),
      createdAt: i.date(),
    }),
    verifications: i.entity({
      expiresAt: i.date().indexed(),
      identifier: i.string(),
      value: i.string(),
      updatedAt: i.date(),
      createdAt: i.date().indexed(),
    }),
    // Optional entities for additional features (public profile example)
    profiles: i.entity({
      image: i.string(),
      name: i.string(),
      updatedAt: i.date(),
      createdAt: i.date(),
    }),
    subscriptions: i.entity({
      plan: i.string(),
      referenceId: i.string().indexed(),
      stripeCustomerId: i.string().optional(),
      stripeSubscriptionId: i.string().optional(),
      status: i.string(),
      periodStart: i.string().optional(),
      periodEnd: i.string().optional(),
      cancelAtPeriodEnd: i.boolean().optional(),
      seats: i.number().optional(),
      trialStart: i.date().optional(),
      trialEnd: i.date().optional(),
    }),
    contactSubmissions: i.entity({
      description: i.string(),
      email: i.string(),
      userId: i.string().optional().indexed(),
      handled: i.boolean().optional(),
    }),
    newsletter: i.entity({
      name: i.string().optional(),
      email: i.string().unique().indexed(),
      createdAt: i.date(),
    }),
    healthAreas: i.entity({
      // So we cannot create duplicate health areas for the same user
      userIdSlug: i.string().unique().optional(),
      symbol: i.string(),
      slug: i.string(),
      title: i.string(),
      color: i.string().optional(),
      description: i.string().optional(),
      goalOrder: i.json<string[]>().optional(),
      routineOrder: i.json<string[]>().optional(),
      updatedAt: i.date(),
      createdAt: i.date(),
    }),
    goals: i.entity({
      symbol: i.string().optional(),
      title: i.string(),
      description: i.string().optional(),
      aiGenerated: i.boolean().optional(),
      aiAccepted: i.boolean().optional(),
      targetDate: i.string().optional(),
      completionDate: i.string().optional(),
      originalGoal: i.json<{ goalId: string; userId: string }>().optional(),
      updatedAt: i.date(),
      createdAt: i.date(),
    }),
    routines: i.entity({
      symbol: i.string().optional(),
      title: i.string(),
      description: i.string().optional(),
      aiGenerated: i.boolean().optional(),
      aiAccepted: i.boolean().optional(),
      dailyRoutine: i.boolean().optional(),
      timeOptional: i.boolean().optional(),
      selectedDays:
        i.json<
          {
            day: "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday";
            datetime: string;
          }[]
        >(),
      steps: i.json<{ id: string; title: string; description: string }[]>(),
      color: i.string().optional(),
      collapsed: i.boolean().optional(),
      originalRoutine: i.json<{ routineId: string; userId: string }>().optional(),
      createdAt: i.date(),
      updatedAt: i.date(),
    }),
    daysCompleted: i.entity({
      date: i.string().indexed(),
      stepsCompleted: i.json<string[]>(),
      completed: i.boolean(),
      createdAt: i.date(),
      updatedAt: i.date(),
    }),
  },
  links: {
    healthAreasUser: {
      forward: {
        on: "healthAreas",
        has: "one",
        label: "userId",
        onDelete: "cascade",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "healthAreas",
      },
    },
    goalsUser: {
      forward: {
        on: "goals",
        has: "one",
        label: "userId",
        onDelete: "cascade",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "goals",
      },
    },
    routinesUser: {
      forward: {
        on: "routines",
        has: "one",
        label: "userId",
        onDelete: "cascade",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "routines",
      },
    },
    healthAreasGoals: {
      forward: {
        on: "healthAreas",
        has: "many",
        label: "goals",
      },
      reverse: {
        on: "goals",
        has: "one",
        label: "healthArea",
      },
    },
    healthAreasRoutines: {
      forward: {
        on: "healthAreas",
        has: "many",
        label: "routines",
      },
      reverse: {
        on: "routines",
        has: "one",
        label: "healthArea",
      },
    },
    routinesDaysCompleted: {
      forward: {
        on: "routines",
        has: "many",
        label: "daysCompleted",
      },
      reverse: {
        on: "daysCompleted",
        has: "one",
        label: "routine",
        onDelete: "cascade",
      },
    },
    daysCompletedUser: {
      forward: {
        on: "daysCompleted",
        has: "one",
        label: "userId",
        onDelete: "cascade",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "daysCompleted",
      },
    },
    // Required links for auth
    users$user: {
      forward: {
        on: "users",
        has: "one",
        label: "$user",
        onDelete: "cascade",
      },
      reverse: {
        on: "$users",
        has: "one",
        label: "user",
      },
    },
    sessionsUser: {
      forward: {
        on: "sessions",
        has: "one",
        label: "user",
        onDelete: "cascade",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "sessions",
      },
    },
    accountsUser: {
      forward: {
        on: "accounts",
        has: "one",
        label: "user",
        onDelete: "cascade",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "accounts",
      },
    },
    // Optional links (public profile example)
    profilesUser: {
      forward: {
        on: "profiles",
        has: "one",
        label: "user",
        onDelete: "cascade",
      },
      reverse: {
        on: "users",
        has: "one",
        label: "profile",
      },
    },
  },
});

// This helps TypeScript display nicer intellisense
type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
